import React, { Suspense, lazy } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useWidgetTitle } from '../../hooks/useWidgetTitle';

const AthleteOverview = lazy(() => import('../Widgets/AthleteOverview'));
const StravaConnect = lazy(() => import('../Widgets/StravaConnect'));
const RecentActivities = lazy(() => import('../Widgets/RecentActivities'));
const AcwrWidget = lazy(() => import('../Widgets/AcwrWidget'));
const KpiAveragesWidget = lazy(() => import('../Widgets/KpiAveragesWidget'));
const PodcastOverview = lazy(() => import('../Widgets/PodcastOverview'));
const AgentChatWidget = lazy(() => import('../Widgets/AgentChatWidget'));
const UserContentWidget = lazy(() => import('../Widgets/UserContentWidget'));
const TrainingSessionsWidget = lazy(() => import('../Widgets/TrainingSessionsWidget'));
const TrainingExercisesWidget = lazy(() => import('../Widgets/TrainingExercisesWidget'));
const ZitiusDailyBriefWidget = lazy(() => import('../Widgets/ZitiusDailyBriefWidget'));
const JitsiMeetWidget = lazy(() => import('../Widgets/JitsiMeetWidget'));
const SkillLoadWidget = lazy(() => import('../Widgets/SkillLoadWidget'));
const SkillDistributionWidget = lazy(() => import('../Widgets/SkillDistributionWidget'));
const KpiStripWidget = lazy(() => import('../Widgets/KpiStripWidget'));
const CoachNavWidget = lazy(() => import('../Widgets/CoachNavWidget'));
const AdminCoachingNavWidget = lazy(() => import('../Widgets/AdminCoachingNavWidget'));
const AdminCoachingSourcesChartWidget = lazy(() => import('../Widgets/AdminCoachingSourcesChartWidget'));
const AdminCoachingTopicsChartWidget = lazy(() => import('../Widgets/AdminCoachingTopicsChartWidget'));
const AthleteHealthBodyMapWidget = lazy(() => import('../Widgets/Health/AthleteHealthBodyMapWidget'));
const AthleteHealthInjuryLogWidget = lazy(() => import('../Widgets/Health/AthleteHealthInjuryLogWidget'));
const AthleteHealthTimelineWidget = lazy(() => import('../Widgets/Health/AthleteHealthTimelineWidget'));
const AthleteRecoveryTrendWidget = lazy(() => import('../Widgets/Health/AthleteRecoveryTrendWidget'));
const AthleteStressTrendWidget = lazy(() => import('../Widgets/Health/AthleteStressTrendWidget'));
const AthleteHealthVitalsOverviewWidget = lazy(() => import('../Widgets/Health/AthleteHealthVitalsOverviewWidget'));
const AthleteHealthVitalsSleepWidget = lazy(() => import('../Widgets/Health/AthleteHealthVitalsSleepWidget'));
const AthleteHealthVitalsHrvWidget = lazy(() => import('../Widgets/Health/AthleteHealthVitalsHrvWidget'));
const AthleteHealthVitalsRhrWidget = lazy(() => import('../Widgets/Health/AthleteHealthVitalsRhrWidget'));
const RacePredictionsWidget = lazy(() => import('../Widgets/RacePredictionsWidget'));
const GoldenZonesWidget = lazy(() => import('../Widgets/GoldenZonesWidget'));
const PlanPreferencesWidget = lazy(() => import('../Widgets/PlanPreferencesWidget'));
const PlanScenarioSelectorWidget = lazy(() => import('../Widgets/PlanScenarioSelectorWidget'));
const PlanGoalsWidget = lazy(() => import('../Widgets/PlanGoalsWidget'));
const StrydApiSandboxWidget = lazy(() => import('../Widgets/StrydApiSandboxWidget'));

const widgetComponents = {
  athlete_overview: AthleteOverview,
  athlete_strava_connect: StravaConnect,
  athlete_kpi_averages: KpiAveragesWidget,
  athlete_recent_activities: RecentActivities,
  athlete_acwr: AcwrWidget,
  athlete_agent_chat: AgentChatWidget,
  athlete_user_content: UserContentWidget,
  athlete_training_sessions: TrainingSessionsWidget,
  athlete_training_exercises: TrainingExercisesWidget,
  athlete_zitius_daily: ZitiusDailyBriefWidget,
  athlete_jitsi_meet: JitsiMeetWidget,
  athlete_skill_load: SkillLoadWidget,
  athlete_training_focus: SkillLoadWidget,
  athlete_skill_distribution: SkillDistributionWidget,
  athlete_kpi_strip: KpiStripWidget,
  podcast_overview: PodcastOverview,
  coach_nav: CoachNavWidget,
  admin_coaching_nav: AdminCoachingNavWidget,
  admin_coaching_sources_chart: AdminCoachingSourcesChartWidget,
  admin_coaching_topics_chart: AdminCoachingTopicsChartWidget,
  athlete_health_body_map: AthleteHealthBodyMapWidget,
  athlete_health_injury_log: AthleteHealthInjuryLogWidget,
  athlete_health_timeline: AthleteHealthTimelineWidget,
  athlete_recovery_trend: AthleteRecoveryTrendWidget,
  athlete_stress_trend: AthleteStressTrendWidget,
  athlete_health_vitals_overview: AthleteHealthVitalsOverviewWidget,
  athlete_health_vitals_sleep: AthleteHealthVitalsSleepWidget,
  athlete_health_vitals_hrv: AthleteHealthVitalsHrvWidget,
  athlete_health_vitals_rhr: AthleteHealthVitalsRhrWidget,
  athlete_race_predictions: RacePredictionsWidget,
  athlete_golden_zones: GoldenZonesWidget,
  athlete_plan_preferences: PlanPreferencesWidget,
  athlete_plan_scenario_selector: PlanScenarioSelectorWidget,
  athlete_plan_goals: PlanGoalsWidget,
  stryd_api_sandbox: StrydApiSandboxWidget,
};

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  render() {
    if (this.state.hasError) {
      return this.props.fallback
        ? this.props.fallback({ error: this.state.error })
        : null;
    }
    return this.props.children;
  }
}

const WidgetRenderer = ({ widget }) => {
  const { t } = useLanguage();
  const title = useWidgetTitle(widget);

  const LoadingFallback = () => (
    <div className='flex flex-col items-center justify-center h-full p-4'>
      <Loader2 className='h-8 w-8 animate-spin text-primary mb-2' />
      <span className='text-sm text-muted-foreground'>{t('dashboard.widgetLoading', { name: title })}</span>
    </div>
  );

  const ErrorFallback = ({ error }) => (
    <div className='flex flex-col items-center justify-center h-full p-4 text-center'>
      <AlertCircle className='h-12 w-12 text-destructive mb-3' />
      <h4 className='font-semibold text-sm mb-1'>{t('dashboard.widgetLoadFailed')}</h4>
      <p className='text-sm text-muted-foreground'>{title}</p>
      <small className='text-xs text-destructive'>{error?.message}</small>
    </div>
  );

  const Component = widgetComponents[widget.widget_key];

  if (!Component) {
    return (
      <div className='flex flex-col items-center justify-center h-full p-4 text-center'>
        <AlertCircle className='h-8 w-8 text-muted-foreground mb-2' />
        <p className='text-sm text-muted-foreground'>{t('dashboard.widgetUnknown', { key: widget.widget_key })}</p>
      </div>
    );
  }

  return (
    <div className='h-full w-full'>
      <Suspense fallback={<LoadingFallback />}>
        <ErrorBoundary fallback={ErrorFallback}>
          <Component config={widget.config} widget={widget} />
        </ErrorBoundary>
      </Suspense>
    </div>
  );
};

export default WidgetRenderer;
